import { useState, useMemo } from 'react';
import type { MenuItem, Category } from '@/lib/data';
import type { CartItem } from '../page';

interface MenuScreenProps {
  menuItems: MenuItem[];
  categories: Category[];
  cart: CartItem[];
  onItemClick: (item: MenuItem) => void;
  onCartClick: () => void;
  /** Plan gate: when true, this is a digital-menu-only view (no ordering). */
  browseOnly?: boolean;
}

const foodTypeConfig: Record<string, { icon: string; color: string }> = {
  veg: { icon: 'ri-leaf-fill', color: 'text-green-600' },
  'non-veg': { icon: 'ri-circle-fill', color: 'text-red-500' },
  egg: { icon: 'ri-circle-fill', color: 'text-amber-500' },
};

export default function MenuScreen({ menuItems, categories, cart, onItemClick, onCartClick, browseOnly = false }: MenuScreenProps) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');

  const cartCount = cart.reduce((sum, ci) => sum + ci.quantity, 0);
  const cartTotal = cart.reduce((sum, ci) => {
    const price = ci.selectedVariant ? ci.selectedVariant.price : ci.menuItem.price;
    const addonsTotal = ci.selectedAddons.reduce((a, ad) => a + ad.price, 0);
    return sum + (price + addonsTotal) * ci.quantity;
  }, 0);

  const filteredItems = useMemo(() => {
    let items = menuItems.filter((i) => i.available);
    if (activeCategory !== 'all') {
      items = items.filter((i) => i.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          (i.description ?? '').toLowerCase().includes(q) ||
          (i.category ?? '').toLowerCase().includes(q),
      );
    }
    return items;
  }, [activeCategory, search, menuItems]);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background-50/95 backdrop-blur-sm border-b border-background-200">
        <div className="px-4 pt-3 pb-3">
          {/* Top row */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-lg font-semibold text-foreground-950 font-heading">Our Menu</h1>
              <p className="text-xs text-foreground-500">{menuItems.filter((i) => i.available).length} items available</p>
            </div>
            {!browseOnly && (
              <button
                onClick={onCartClick}
                className="relative w-11 h-11 flex items-center justify-center bg-accent-500 hover:bg-accent-600 text-background-50 rounded-xl transition-colors cursor-pointer"
              >
                <i className="ri-shopping-bag-2-line text-xl"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-foreground-950 text-background-50 text-[10px] font-bold rounded-full">
                    {cartCount}
                  </span>
                )}
              </button>
            )}
          </div>

          {browseOnly && (
            <div className="mb-3 flex items-start gap-2 rounded-lg bg-background-100 border border-background-200 px-3 py-2 text-xs text-foreground-600">
              <i className="ri-information-line text-foreground-400 mt-0.5"></i>
              <span>Online ordering is not available here. Please browse the menu and order with a member of staff.</span>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search dishes, categories..."
              className="w-full pl-9 pr-4 py-2.5 bg-background-100 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
              >
                <i className="ri-close-circle-fill text-foreground-400 text-sm"></i>
              </button>
            )}
          </div>
        </div>

        {/* Category tabs */}
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
          {[{ id: 'all', name: 'All', icon: 'ri-restaurant-2-line' }, ...categories].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon || 'ri-restaurant-2-line'} text-xs`}></i>
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Items grid */}
      <div className="flex-1 px-4 py-4 pb-24">
        {filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-foreground-400">
            <i className="ri-search-line text-4xl mb-3"></i>
            <p className="text-sm">No dishes match "{search}"</p>
            <button onClick={() => { setSearch(''); setActiveCategory('all'); }} className="mt-2 text-primary-500 text-sm font-medium cursor-pointer">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {filteredItems.map((item) => (
              <MenuItemCard key={item.id} item={item} onClick={() => onItemClick(item)} />
            ))}
          </div>
        )}
      </div>

      {/* Floating cart bar */}
      {!browseOnly && cartCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 px-4 pb-4 pt-2 bg-gradient-to-t from-background-50 via-background-50 to-transparent">
          <button
            onClick={onCartClick}
            className="w-full flex items-center justify-between bg-accent-500 hover:bg-accent-600 text-background-50 rounded-2xl px-5 py-3.5 transition-all active:scale-[0.98] cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <i className="ri-shopping-bag-2-fill text-xl"></i>
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-background-50 text-accent-500 text-[10px] font-bold rounded-full">
                  {cartCount}
                </span>
              </div>
              <span className="text-sm font-semibold">View Cart</span>
            </div>
            <span className="text-sm font-bold">₹{cartTotal}</span>
          </button>
        </div>
      )}

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

function MenuItemCard({ item, onClick }: { item: MenuItem; onClick: () => void }) {
  const ft = foodTypeConfig[item.foodType];
  const showVariants = item.variants.length > 0;
  const priceDisplay = showVariants
    ? `₹${Math.min(...item.variants.map((v) => v.price))} – ₹${Math.max(...item.variants.map((v) => v.price))}`
    : `₹${item.price}`;

  return (
    <button
      onClick={onClick}
      className={`flex bg-background-50 border border-background-200 rounded-xl overflow-hidden cursor-pointer transition-all active:scale-[0.98] text-left w-full ${
        !item.available ? 'opacity-60 pointer-events-none' : ''
      }`}
    >
      {/* Image */}
      <div className="w-28 h-28 flex-shrink-0 relative">
        {item.image ? (
          <img src={item.image} alt={item.name} className="w-full h-full object-cover object-center" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-background-100 flex items-center justify-center">
            <i className="ri-restaurant-2-line text-2xl text-foreground-300"></i>
          </div>
        )}
        {!item.available && (
          <div className="absolute inset-0 bg-background-50/70 flex items-center justify-center">
            <span className="text-xs font-semibold text-foreground-500">Unavailable</span>
          </div>
        )}
        {item.recommended && (
          <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-[10px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5">
            <i className="ri-star-fill text-[8px]"></i> Best
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <i className={`${ft.icon} text-[10px] ${ft.color}`}></i>
            <h3 className="text-sm font-semibold text-foreground-950 truncate font-heading">{item.name}</h3>
          </div>
          <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">{item.description}</p>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-sm font-bold text-foreground-950">{priceDisplay}</span>
          <div className="flex items-center gap-1">
            {item.addons.length > 0 && (
              <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-md">
                +{item.addons.length} addons
              </span>
            )}
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-600">
              <i className="ri-add-line text-sm"></i>
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}
